/**
 * /app/analysis — landing del módulo de Análisis estructural.
 *
 * Lista de cards: vigas (activo), columnas/losas/muros/zapatas (soon).
 */

import { Link } from 'react-router-dom';
import { Square, Columns3, Layers, Frame, Activity, ArrowRight } from 'lucide-react';
import { FootingIcon } from '@/components/icons/FootingIcon';

interface ModuleCard {
  to: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  available: boolean;
}

const MODULES: ModuleCard[] = [
  {
    to: '/app/analysis/beams',
    label: 'Vigas',
    description: 'Calcula Mu y Vu desde geometría + apoyo + uso + cargas (distribuidas + puntuales).',
    icon: <Square size={22} strokeWidth={1.6} />,
    available: true,
  },
  {
    to: '/app/analysis/columns',
    label: 'Columnas',
    description: 'Calcula Pu, Mu_x, Mu_y por gravedad + sismo método estático E.030.',
    icon: <Columns3 size={22} strokeWidth={1.6} />,
    available: true,
  },
  {
    to: '/app/analysis/slabs',
    label: 'Losas',
    description: 'Calcula momentos por dirección — losa aligerada o maciza, 1D y 2D.',
    icon: <Layers size={22} strokeWidth={1.6} />,
    available: true,
  },
  {
    to: '/app/analysis/walls',
    label: 'Muros',
    description: 'Cortante basal + envolvente sísmica para muros estructurales.',
    icon: <Frame size={22} strokeWidth={1.6} />,
    available: true,
  },
  {
    to: '/app/analysis/footings',
    label: 'Zapatas',
    description: 'Dimensiona la zapata y devuelve Mu/Vu por presión de suelo factorada.',
    icon: <FootingIcon size={22} strokeWidth={1.6} />,
    available: true,
  },
];

export function AnalysisIndexPage() {
  return (
    <div className="px-8 py-8">
      <div className="mb-6">
        <div
          className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em]"
          style={{ color: 'var(--text-muted)' }}
        >
          <Activity size={12} strokeWidth={1.8} /> Análisis estructural
        </div>
        <h1 className="mt-1 text-3xl" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
          Análisis estructural
        </h1>
        <p className="mt-2 max-w-2xl text-sm" style={{ color: 'var(--text-secondary)' }}>
          Calcula las fuerzas internas (Mu, Vu, Pu) a partir de la geometría,
          los apoyos y las cargas aplicadas. Después envía los resultados al
          módulo de Diseño para calcular el refuerzo.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {MODULES.map((m) => (
          <ModuleTile key={m.to} m={m} />
        ))}
      </div>
    </div>
  );
}

function ModuleTile({ m }: { m: ModuleCard }) {
  const inner = (
    <div
      className="group relative h-full rounded-[var(--radius-md)] border p-5 transition-all"
      style={{
        background: 'var(--surface-1)',
        borderColor: 'var(--border-default)',
        opacity: m.available ? 1 : 0.55,
      }}
    >
      <div
        className="mb-3 flex h-10 w-10 items-center justify-center rounded-[var(--radius-sm)]"
        style={{ background: 'var(--surface-2)', color: 'var(--accent)' }}
      >
        {m.icon}
      </div>
      <div className="flex items-center gap-2">
        <h3
          className="text-base"
          style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--text-primary)' }}
        >
          {m.label}
        </h3>
        {!m.available && (
          <span
            className="rounded-[var(--radius-pill)] border px-2 py-0.5 text-[9px] uppercase tracking-wider"
            style={{ borderColor: 'var(--border-default)', color: 'var(--text-muted)' }}
          >
            Soon
          </span>
        )}
      </div>
      <p className="mt-1.5 text-[12.5px]" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
        {m.description}
      </p>
      {m.available && (
        <div
          className="mt-3 inline-flex items-center gap-1 text-[12px] transition-colors group-hover:gap-2"
          style={{ color: 'var(--accent)' }}
        >
          Abrir <ArrowRight size={12} strokeWidth={2} />
        </div>
      )}
    </div>
  );

  return m.available ? (
    <Link to={m.to} className="no-underline">
      {inner}
    </Link>
  ) : (
    <div className="cursor-not-allowed">{inner}</div>
  );
}
